import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  Plus, Trash2, MapPin, Car, Calculator, Hotel, Copy, ChevronDown, ChevronUp,
  GripVertical, Package, ArrowRight, Calendar, DollarSign
} from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import { cn } from '../lib/utils';

/**
 * ManualPackageBuilder - For Travel Agents without rate masters
 * Allows free-text entry of places, hotels, rooms, and costs
 * Supports multiple package options (Standard, Deluxe, Premium, etc.)
 */
const ManualPackageBuilder = ({ 
  formData, 
  setFormData,
}) => {
  // Initialize manual package data
  const [manualData, setManualData] = useState(() => {
    const existing = formData.manual_package_data;
    if (existing && existing.route && existing.options) {
      return {
        ...existing,
        p2p_transfers: existing.p2p_transfers || [],
        itinerary: existing.itinerary || [],
      };
    }
    return {
      route: [{ id: Date.now(), place_name: '', nights: 1, order: 1 }],
      options: [{
        id: Date.now(),
        option_name: 'Standard',
        accommodation: [],
        package_transfer: { vehicle_name: '', num_vehicles: 1, rate_per_vehicle: '', gst_pct: 5 },
        gross_total: 0,
        gross_override: '',
        collapsed: false,
      }],
      p2p_transfers: [],
      itinerary: [],
    };
  });

  // Expand/collapse sections
  const [expandedP2P, setExpandedP2P] = useState(true);
  const [expandedItinerary, setExpandedItinerary] = useState(true);

  // Sync accommodation rows + compute totals + bubble to parent in ONE pass.
  // Consolidating the previous two effects (route→accommodation rebuild and
  // manualData→formData bubble) eliminates the race where the parent received
  // stale accommodation for one render after a route change. The Alleppey-row
  // sync bug was a symptom of that race.
  useEffect(() => {
    // UX-04 — Auto-populate `num_rooms` reactively from `formData.no_adults`
    // and room_config. We mark each row with `_rooms_auto:true` while the
    // value is system-derived. The `updateAccommodation` handler clears that
    // flag the moment the user edits Rooms, so a manual override survives
    // subsequent recomputes (e.g., when adults later change).
    const autoRoomsCount =
      (formData.room_config?.double_rooms ||
        formData.double_rooms ||
        Math.max(1, Math.ceil((parseInt(formData.no_adults) || 2) / 2))) +
      (formData.room_config?.single_rooms || formData.single_rooms || 0);
    // Derive rebuilt accommodation from current route synchronously per option,
    // preserving any user-entered hotel data for stops still present.
    const optionsRebuilt = manualData.options.map(option => {
      const rebuiltAccommodation = manualData.route.map(stop => {
        const existing = option.accommodation.find(
          a => a.place_name === stop.place_name && a.nights === stop.nights
        ) || option.accommodation.find(
          (_, idx) => idx === manualData.route.findIndex(r => r.id === stop.id)
        );
        // Recompute rooms when user hasn't explicitly edited this row
        // (i.e., `_rooms_auto` is still true OR the row is brand-new).
        const isAutoRooms = existing?._rooms_auto !== false;
        const numRooms = isAutoRooms ? autoRoomsCount : existing.num_rooms;
        return {
          place_name: stop.place_name,
          nights: stop.nights,
          hotel_name: existing?.hotel_name || '',
          room_type: existing?.room_type || '',
          meal_plan: existing?.meal_plan || 'CP',
          num_rooms: numRooms,
          _rooms_auto: isAutoRooms,
          room_rate_per_night: existing?.room_rate_per_night || '',
          gst_pct: existing?.gst_pct ?? 12,
          total_cost: existing?.total_cost || 0,
        };
      });

      // Compute totals using the freshly-rebuilt accommodation.
      let accommodationTotal = 0;
      let accommodationGst = 0;
      rebuiltAccommodation.forEach(acc => {
        const cost = parseFloat(acc.room_rate_per_night) || 0;
        const rooms = parseInt(acc.num_rooms) || 1;
        const nights = parseInt(acc.nights) || 1;
        const gstPct = parseFloat(acc.gst_pct) || 0;
        const total = cost * rooms * nights;
        const gstAmount = total * (gstPct / 100);
        acc.total_cost = total;
        acc.gst_amount = gstAmount;
        accommodationTotal += total;
        accommodationGst += gstAmount;
      });

      const ratePerVehicle = parseFloat(option.package_transfer.rate_per_vehicle) || 0;
      const numVehicles = parseInt(option.package_transfer.num_vehicles) || 1;
      const transferGstPct = parseFloat(option.package_transfer.gst_pct) || 0;
      const transferBase = ratePerVehicle * numVehicles;
      const transferGst = transferBase * (transferGstPct / 100);
      const transferTotal = transferBase + transferGst;

      const grossTotal = accommodationTotal + accommodationGst + transferTotal;

      return {
        ...option,
        accommodation: rebuiltAccommodation,
        accommodation_total: accommodationTotal,
        accommodation_gst: accommodationGst,
        transfer_base: transferBase,
        transfer_gst: transferGst,
        transfer_total: transferTotal,
        gross_total: option.gross_override ? parseFloat(option.gross_override) : grossTotal,
      };
    });

    const totalNights = manualData.route.reduce((sum, stop) => sum + (parseInt(stop.nights) || 0), 0);
    const totalDays = totalNights + 1;

    let p2pTotal = 0;
    let p2pGst = 0;
    manualData.p2p_transfers.forEach(p2p => {
      const cost = parseFloat(p2p.cost) || 0;
      const qty = parseInt(p2p.quantity) || 1;
      const gstPct = parseFloat(p2p.gst_pct) || 0;
      const base = cost * qty;
      const gst = base * (gstPct / 100);
      p2pTotal += base;
      p2pGst += gst;
    });

    // Update local state ONLY if the rebuilt options actually differ
    // (prevents an infinite render loop since this effect depends on manualData).
    // UX-04 — also resync when an auto-computed `num_rooms` changes (e.g.,
    // user updates `no_adults` or `room_config`), so the visible Rooms input
    // stays consistent with the formula.
    const needsLocalSync = optionsRebuilt.some((o, i) => {
      const cur = manualData.options[i];
      if (!cur || cur.accommodation.length !== o.accommodation.length) return true;
      return o.accommodation.some((acc, j) => {
        const prev = cur.accommodation[j];
        return prev && prev._rooms_auto !== false && prev.num_rooms !== acc.num_rooms;
      });
    });
    if (needsLocalSync) {
      setManualData(prev => ({ ...prev, options: optionsRebuilt }));
    }

    setFormData(prev => ({
      ...prev,
      manual_package_data: {
        ...manualData,
        options: optionsRebuilt,
        total_nights: totalNights,
        total_days: totalDays,
        p2p_total: p2pTotal + p2pGst,
        p2p_base: p2pTotal,
        p2p_gst: p2pGst,
      },
      transfer_days: totalDays,
    }));
  }, [manualData, formData.room_config, formData.no_adults, formData.double_rooms, formData.single_rooms, setFormData]);

  // Route handlers
  const addStop = () => {
    setManualData(prev => ({
      ...prev,
      route: [...prev.route, {
        id: Date.now(),
        place_name: '',
        nights: 1,
        order: prev.route.length + 1,
      }],
    }));
  };

  const removeStop = (stopId) => {
    if (manualData.route.length <= 1) return;
    setManualData(prev => ({
      ...prev,
      route: prev.route.filter(s => s.id !== stopId).map((s, i) => ({ ...s, order: i + 1 })),
    }));
  };

  const updateStop = (stopId, field, value) => {
    setManualData(prev => ({
      ...prev,
      route: prev.route.map(s => s.id === stopId ? { ...s, [field]: value } : s),
    }));
  };

  // Option handlers
  const addOption = () => {
    const optionNames = ['Standard', 'Deluxe', 'Premium', 'Luxury', 'Ultra Luxury'];
    const usedNames = manualData.options.map(o => o.option_name);
    const nextName = optionNames.find(n => !usedNames.includes(n)) || `Option ${manualData.options.length + 1}`;
    
    setManualData(prev => ({
      ...prev,
      options: [...prev.options, {
        id: Date.now(),
        option_name: nextName,
        accommodation: prev.route.map(stop => ({
          place_name: stop.place_name,
          nights: stop.nights,
          hotel_name: '',
          room_type: '',
          meal_plan: 'CP',
          num_rooms: (formData.room_config?.double_rooms || formData.double_rooms || Math.max(1, Math.ceil((parseInt(formData.no_adults) || 2) / 2))) + (formData.room_config?.single_rooms || formData.single_rooms || 0),
          _rooms_auto: true,
          room_rate_per_night: '',
          gst_pct: 12,  // Default GST for accommodation
          total_cost: 0,
        })),
        package_transfer: { vehicle_name: '', num_vehicles: 1, rate_per_vehicle: '', gst_pct: 5 },  // Fixed: use rate_per_vehicle and gst_pct
        gross_total: 0,
        gross_override: '',
        collapsed: false,
      }],
    }));
  };

  const copyOption = (optionId) => {
    const optionToCopy = manualData.options.find(o => o.id === optionId);
    if (!optionToCopy) return;
    
    setManualData(prev => ({
      ...prev,
      options: [...prev.options, {
        ...JSON.parse(JSON.stringify(optionToCopy)),
        id: Date.now(),
        option_name: `${optionToCopy.option_name} (Copy)`,
      }],
    }));
  };

  const removeOption = (optionId) => {
    if (manualData.options.length <= 1) return;
    setManualData(prev => ({
      ...prev,
      options: prev.options.filter(o => o.id !== optionId),
    }));
  };

  const toggleOptionCollapse = (optionId) => {
    setManualData(prev => ({
      ...prev,
      options: prev.options.map(o => 
        o.id === optionId ? { ...o, collapsed: !o.collapsed } : o
      ),
    }));
  };

  const updateOption = (optionId, field, value) => {
    setManualData(prev => ({
      ...prev,
      options: prev.options.map(o => o.id === optionId ? { ...o, [field]: value } : o),
    }));
  };

  const updateAccommodation = (optionId, stopIndex, field, value) => {
    setManualData(prev => ({
      ...prev,
      options: prev.options.map(o => {
        if (o.id !== optionId) return o;
        const newAccommodation = [...o.accommodation];
        const next = { ...newAccommodation[stopIndex], [field]: value };
        // UX-04 — once the user edits the Rooms field, mark the row as
        // manually-edited so the auto-recompute on adults/room_config
        // changes doesn't clobber their value.
        if (field === 'num_rooms') {
          next._rooms_auto = false;
        }
        newAccommodation[stopIndex] = next;
        return { ...o, accommodation: newAccommodation };
      }),
    }));
  };

  const updateTransfer = (optionId, field, value) => {
    setManualData(prev => ({
      ...prev,
      options: prev.options.map(o => {
        if (o.id !== optionId) return o;
        return { ...o, package_transfer: { ...o.package_transfer, [field]: value } };
      }),
    }));
  };

  // P2P Transfer handlers
  const addP2PTransfer = () => {
    const places = manualData.route.filter(s => s.place_name);
    setManualData(prev => ({
      ...prev,
      p2p_transfers: [...prev.p2p_transfers, {
        id: Date.now(),
        from_place: places[0]?.place_name || '',
        to_place: places[1]?.place_name || '',
        transport_mode: 'Car',
        price_type: 'FIXED',
        quantity: 1,
        cost: '',
        gst_pct: 5,
      }],
    }));
  };

  const updateP2PTransfer = (id, field, value) => {
    setManualData(prev => ({
      ...prev,
      p2p_transfers: prev.p2p_transfers.map(p => 
        p.id === id ? { ...p, [field]: value } : p
      ),
    }));
  };

  const removeP2PTransfer = (id) => {
    setManualData(prev => ({
      ...prev,
      p2p_transfers: prev.p2p_transfers.filter(p => p.id !== id),
    }));
  };

  // Batch 1.D — Miscellaneous Costs editor surfaced inside Manual Step 1.
  // Source of truth is `formData.misc_costs` (top-level), same store used
  // by the Step-3 preview's editor. Mutating here keeps the two editors in
  // sync without duplicating state. Cost types kept compact for Step 1.
  const miscCostsList = formData.misc_costs || [];
  const addMiscCost = () => {
    const newCost = {
      id: Date.now(),
      remarks: '',
      cost_type: 'total',
      amount: 0,
      quantity: 1,
      gst_percentage: 0,
      is_optional: false,
    };
    setFormData(prev => ({ ...prev, misc_costs: [...(prev.misc_costs || []), newCost] }));
  };
  const updateMiscCost = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      misc_costs: (prev.misc_costs || []).map(c => (c.id === id ? { ...c, [field]: value } : c)),
    }));
  };
  const removeMiscCost = (id) => {
    setFormData(prev => ({
      ...prev,
      misc_costs: (prev.misc_costs || []).filter(c => c.id !== id),
    }));
  };
  const miscIncludedTotal = miscCostsList.reduce((sum, c) => {
    if (c.is_optional) return sum;
    const adults = parseInt(formData.no_adults) || 1;
    const qty = c.cost_type === 'per_person' ? adults : (parseInt(c.quantity) || 1);
    const base = (parseFloat(c.amount) || 0) * qty;
    const gst = base * ((parseFloat(c.gst_percentage) || 0) / 100);
    return sum + base + gst;
  }, 0);

  // Itinerary handlers
  const addItineraryDay = () => {
    const newDay = manualData.itinerary.length + 1;
    setManualData(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { 
        day: newDay, 
        title: '', 
        description: '',
        overnight_at: manualData.route[0]?.place_name || '',
      }],
    }));
  };

  const updateItinerary = (idx, field, value) => {
    setManualData(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((item, i) => 
        i === idx ? { ...item, [field]: value } : item
      ),
    }));
  };

  const deleteItineraryDay = (idx) => {
    if (manualData.itinerary.length <= 1) return;
    setManualData(prev => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== idx).map((item, i) => ({ ...item, day: i + 1 })),
    }));
  };

  // Auto-generate itinerary from route
  const autoGenerateItinerary = () => {
    const places = manualData.route.filter(s => s.place_name);
    if (places.length === 0) return;

    let dayNum = 1;
    const newItinerary = [];

    // Day 1: Arrival
    newItinerary.push({
      day: dayNum,
      title: `Arrival - ${places[0].place_name}`,
      description: `Arrive at ${places[0].place_name}. Check in to the hotel. Rest of the day at leisure.`,
      overnight_at: places[0].place_name,
    });
    dayNum++;

    // Generate days for each place
    places.forEach((place, placeIdx) => {
      const nights = parseInt(place.nights) || 1;
      
      // For nights > 1, add exploration days
      for (let n = 1; n < nights; n++) {
        newItinerary.push({
          day: dayNum,
          title: `Explore ${place.place_name}`,
          description: `Full day sightseeing in ${place.place_name}. Visit local attractions.`,
          overnight_at: place.place_name,
        });
        dayNum++;
      }

      // Travel to next place (if not last)
      if (placeIdx < places.length - 1) {
        const nextPlace = places[placeIdx + 1];
        newItinerary.push({
          day: dayNum,
          title: `${place.place_name} to ${nextPlace.place_name}`,
          description: `After breakfast, proceed to ${nextPlace.place_name}. Check in and rest.`,
          overnight_at: nextPlace.place_name,
        });
        dayNum++;
      }
    });

    // Last day: Departure
    const lastPlace = places[places.length - 1];
    newItinerary.push({
      day: dayNum,
      title: `Departure from ${lastPlace.place_name}`,
      description: `After breakfast, check out and proceed for departure. Tour ends with wonderful memories.`,
      overnight_at: '',
    });

    setManualData(prev => ({ ...prev, itinerary: newItinerary }));
  };

  // Calculate summary
  const totalNights = manualData.route.reduce((sum, s) => sum + (parseInt(s.nights) || 0), 0);
  const totalDays = totalNights + 1;
  // DISP-02 — Route summary now bookends the stop list with the
  // formData arrival/departure cities so the customer sees a complete
  // entry → tour → exit picture instead of the bare stop sequence.
  // Skips bookends when they duplicate the first/last stop name.
  const stopParts = manualData.route
    .filter(s => s.place_name)
    .map(s => `${s.place_name} (${s.nights}N)`);
  const arrivalCity = (formData.arrival_city || '').trim();
  const departureCity = (formData.departure_city || '').trim();
  const summaryParts = [];
  if (arrivalCity) {
    const firstStopName = stopParts[0] ? stopParts[0].split(' (')[0] : '';
    if (arrivalCity && arrivalCity !== firstStopName) {
      summaryParts.push(`${arrivalCity} (Arrival)`);
    }
  }
  summaryParts.push(...stopParts);
  if (departureCity) {
    const lastStopName = summaryParts[summaryParts.length - 1]
      ? summaryParts[summaryParts.length - 1].split(' (')[0]
      : '';
    if (departureCity !== lastStopName) {
      summaryParts.push(`${departureCity} (Departure)`);
    }
  }
  const routeSummary = summaryParts.join(' → ');

  return (
    <div className="space-y-4" data-testid="manual-package-builder">
      {/* Section A: Route */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
              <MapPin className="w-4 h-4 text-blue-600" />
              Route
            </CardTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addStop}
              className="h-7 text-xs"
              data-testid="add-stop-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Stop
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-2">
            {manualData.route.map((stop, index) => (
              <div 
                key={stop.id} 
                className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200"
                data-testid={`route-stop-${index}`}
              >
                <span className="w-6 h-6 flex items-center justify-center bg-blue-600 text-white text-xs rounded-full font-medium">
                  {index + 1}
                </span>
                <Input
                  type="text"
                  placeholder="Place name (e.g., Munnar)"
                  value={stop.place_name}
                  onChange={(e) => updateStop(stop.id, 'place_name', e.target.value)}
                  className="flex-1 bg-white"
                  data-testid={`route-place-${index}`}
                />
                <div className="flex items-center gap-1">
                  {/* Batch 1.A — Nights selector replaced free-text number
                      input with a dropdown of 1..15 N. Default `1` was the
                      most common edit; a select makes the affordance clearer
                      and prevents nonsense values like 0 or 99. */}
                  <Select
                    value={String(stop.nights || 1)}
                    onValueChange={(v) => updateStop(stop.id, 'nights', parseInt(v) || 1)}
                  >
                    <SelectTrigger className="h-9 w-20 text-sm bg-white" data-testid={`route-nights-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}N</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {manualData.route.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStop(stop.id)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-stop-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          
          {/* Route Summary */}
          {routeSummary && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center justify-between">
                <div className="text-sm text-blue-800">
                  <span className="font-medium">Route:</span> {routeSummary}
                </div>
                <div className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full font-medium">
                  {totalDays}D / {totalNights}N
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section B: Package Options */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Package className="w-4 h-4 text-blue-600" />
            Package Options
          </h3>
          <Button
            type="button"
            size="sm"
            onClick={addOption}
            className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
            data-testid="add-option-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Option
          </Button>
        </div>

        {manualData.options.map((option, optionIndex) => (
          <Card 
            key={option.id} 
            className={cn(
              "border-2 shadow-sm transition-all",
              optionIndex === 0 ? "border-emerald-300 bg-emerald-50/30" :
              optionIndex === 1 ? "border-blue-300 bg-blue-50/30" :
              optionIndex === 2 ? "border-purple-300 bg-purple-50/30" :
              "border-slate-300"
            )}
            data-testid={`option-card-${optionIndex}`}
          >
            <CardHeader className="py-2 px-4 border-b border-slate-200 bg-white/80">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="text"
                    value={option.option_name}
                    onChange={(e) => updateOption(option.id, 'option_name', e.target.value)}
                    className="max-w-[200px] h-8 font-semibold bg-transparent border-dashed"
                    placeholder="Option Name"
                    data-testid={`option-name-${optionIndex}`}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => copyOption(option.id)}
                    className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600"
                    title="Copy Option"
                    data-testid={`copy-option-${optionIndex}`}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  {manualData.options.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(option.id)}
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                      title="Remove Option"
                      data-testid={`remove-option-${optionIndex}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleOptionCollapse(option.id)}
                    className="h-7 w-7 p-0 text-slate-500"
                    data-testid={`toggle-option-${optionIndex}`}
                  >
                    {option.collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>

            {!option.collapsed && (
              <CardContent className="pt-4 space-y-4">
                {/* Accommodation Table */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase flex items-center gap-1 mb-2">
                    <Hotel className="w-3 h-3" />
                    Accommodation
                  </Label>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b">Place</th>
                          <th className="text-center p-2 text-xs font-semibold text-slate-600 border-b w-12">Nts</th>
                          <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b">Hotel Name</th>
                          <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b">Room Name</th>
                          <th className="text-center p-2 text-xs font-semibold text-slate-600 border-b w-16">Meal</th>
                          <th className="text-center p-2 text-xs font-semibold text-slate-600 border-b w-16">Rooms</th>
                          <th className="text-right p-2 text-xs font-semibold text-slate-600 border-b w-24">Cost/Rm/Nt</th>
                          <th className="text-center p-2 text-xs font-semibold text-slate-600 border-b w-16">GST%</th>
                          <th className="text-right p-2 text-xs font-semibold text-slate-600 border-b w-24">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {option.accommodation.map((acc, accIndex) => {
                          const cost = parseFloat(acc.room_rate_per_night) || 0;
                          const rooms = parseInt(acc.num_rooms) || 1;
                          const nights = parseInt(acc.nights) || 1;
                          const gstPct = parseFloat(acc.gst_pct) || 0;
                          const baseTotal = cost * rooms * nights;
                          const gstAmount = baseTotal * (gstPct / 100);
                          const total = baseTotal + gstAmount;
                          
                          return (
                            <tr key={accIndex} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="p-2 text-slate-700 font-medium">{acc.place_name || '-'}</td>
                              <td className="p-2 text-center text-slate-600">{acc.nights}</td>
                              <td className="p-2">
                                <Input
                                  type="text"
                                  placeholder="Hotel name"
                                  value={acc.hotel_name}
                                  onChange={(e) => updateAccommodation(option.id, accIndex, 'hotel_name', e.target.value)}
                                  className="h-8 text-sm bg-white"
                                  data-testid={`acc-hotel-${optionIndex}-${accIndex}`}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="text"
                                  placeholder="Room type"
                                  value={acc.room_type}
                                  onChange={(e) => updateAccommodation(option.id, accIndex, 'room_type', e.target.value)}
                                  className="h-8 text-sm bg-white"
                                  data-testid={`acc-room-${optionIndex}-${accIndex}`}
                                />
                              </td>
                              <td className="p-2">
                                <Select
                                  value={acc.meal_plan}
                                  onValueChange={(v) => updateAccommodation(option.id, accIndex, 'meal_plan', v)}
                                >
                                  <SelectTrigger className="h-8 text-xs bg-white" data-testid={`acc-meal-${optionIndex}-${accIndex}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="EP">EP</SelectItem>
                                    <SelectItem value="CP">CP</SelectItem>
                                    <SelectItem value="MAP">MAP</SelectItem>
                                    <SelectItem value="AP">AP</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="1"
                                  value={acc.num_rooms}
                                  onChange={(e) => updateAccommodation(option.id, accIndex, 'num_rooms', parseInt(e.target.value) || 1)}
                                  className="h-8 text-sm text-center bg-white w-14"
                                  data-testid={`acc-rooms-${optionIndex}-${accIndex}`}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  placeholder="0"
                                  value={acc.room_rate_per_night}
                                  onChange={(e) => updateAccommodation(option.id, accIndex, 'room_rate_per_night', e.target.value)}
                                  className="h-8 text-sm text-right bg-cyan-50 border-cyan-200 w-24"
                                  data-testid={`acc-cost-${optionIndex}-${accIndex}`}
                                />
                              </td>
                              <td className="p-2">
                                <Select
                                  value={String(acc.gst_pct ?? 12)}
                                  onValueChange={(v) => updateAccommodation(option.id, accIndex, 'gst_pct', parseFloat(v))}
                                >
                                  <SelectTrigger className="h-8 text-xs bg-white w-16" data-testid={`acc-gst-${optionIndex}-${accIndex}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="0">0%</SelectItem>
                                    <SelectItem value="5">5%</SelectItem>
                                    <SelectItem value="12">12%</SelectItem>
                                    <SelectItem value="18">18%</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-2 text-right text-slate-700">
                                {formatCurrency(total)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100">
                          <td colSpan="8" className="p-2 text-right text-xs font-semibold text-slate-600">
                            Accommodation Total (incl. GST):
                          </td>
                          <td className="p-2 text-right font-semibold text-slate-800">
                            {formatCurrency(option.accommodation?.reduce((sum, acc) => {
                              const cost = parseFloat(acc.room_rate_per_night) || 0;
                              const rooms = parseInt(acc.num_rooms) || 1;
                              const nights = parseInt(acc.nights) || 1;
                              const gstPct = parseFloat(acc.gst_pct) || 0;
                              const base = cost * rooms * nights;
                              return sum + base + (base * gstPct / 100);
                            }, 0) || 0)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Package Transfer */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase flex items-center gap-1 mb-2">
                    <Car className="w-3 h-3" />
                    Package Transfer (Sightseeing & Local)
                  </Label>
                  <div className="grid grid-cols-5 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Vehicle</Label>
                      <Input
                        type="text"
                        placeholder="e.g., Innova"
                        value={option.package_transfer.vehicle_name}
                        onChange={(e) => updateTransfer(option.id, 'vehicle_name', e.target.value)}
                        className="h-8 bg-white"
                        data-testid={`transfer-vehicle-${optionIndex}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">No. of Vehicles</Label>
                      <Input
                        type="number"
                        min="1"
                        value={option.package_transfer.num_vehicles}
                        onChange={(e) => updateTransfer(option.id, 'num_vehicles', parseInt(e.target.value) || 1)}
                        className="h-8 bg-white text-center"
                        data-testid={`transfer-count-${optionIndex}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-cyan-600 font-semibold">Rate/Vehicle ₹</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={option.package_transfer.rate_per_vehicle}
                        onChange={(e) => updateTransfer(option.id, 'rate_per_vehicle', e.target.value)}
                        className="h-8 bg-cyan-50 border-cyan-200"
                        data-testid={`transfer-rate-${optionIndex}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">GST %</Label>
                      <Select
                        value={String(option.package_transfer.gst_pct ?? 5)}
                        onValueChange={(v) => updateTransfer(option.id, 'gst_pct', parseFloat(v))}
                      >
                        <SelectTrigger className="h-8 text-xs bg-white" data-testid={`transfer-gst-${optionIndex}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0%</SelectItem>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-emerald-600 font-semibold">Total Amount ₹</Label>
                      <div className="h-8 px-3 flex items-center justify-end bg-emerald-50 border border-emerald-200 rounded-md text-sm font-semibold text-emerald-800">
                        {formatCurrency(
                          (parseInt(option.package_transfer.num_vehicles) || 1) * 
                          (parseFloat(option.package_transfer.rate_per_vehicle) || 0) * 
                          (1 + (parseFloat(option.package_transfer.gst_pct ?? 5)) / 100)
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Transfer Calculation Breakdown */}
                  {(parseFloat(option.package_transfer.rate_per_vehicle) > 0) && (
                    <div className="mt-2 text-xs text-slate-500 text-right">
                      {option.package_transfer.num_vehicles || 1} vehicle(s) × ₹{parseFloat(option.package_transfer.rate_per_vehicle || 0).toLocaleString('en-IN')} 
                      {(parseFloat(option.package_transfer.gst_pct ?? 5)) > 0 && ` + ${option.package_transfer.gst_pct ?? 5}% GST`}
                    </div>
                  )}
                </div>

                {/* Option Summary */}
                <div className="p-3 bg-slate-100 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-600">Accommodation (incl. GST):</span>
                        <span className="text-slate-800">
                          {formatCurrency(option.accommodation?.reduce((sum, acc) => {
                            const cost = parseFloat(acc.room_rate_per_night) || 0;
                            const rooms = parseInt(acc.num_rooms) || 1;
                            const nights = parseInt(acc.nights) || 1;
                            const gstPct = parseFloat(acc.gst_pct) || 0;
                            const base = cost * rooms * nights;
                            return sum + base + (base * gstPct / 100);
                          }, 0) || 0)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-600">Package Transfer (incl. GST):</span>
                        <span className="text-slate-800">
                          {formatCurrency(
                            (parseInt(option.package_transfer.num_vehicles) || 1) *
                            (parseFloat(option.package_transfer.rate_per_vehicle) || 0) *
                            (1 + (parseFloat(option.package_transfer.gst_pct ?? 5)) / 100)
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 mb-1">Option Gross Total</div>
                      <div className="text-xl font-bold text-slate-800">
                        {formatCurrency(
                          (option.accommodation?.reduce((sum, acc) => {
                            const cost = parseFloat(acc.room_rate_per_night) || 0;
                            const rooms = parseInt(acc.num_rooms) || 1;
                            const nights = parseInt(acc.nights) || 1;
                            const gstPct = parseFloat(acc.gst_pct ?? 12);
                            const base = cost * rooms * nights;
                            return sum + base + (base * gstPct / 100);
                          }, 0) || 0) + 
                          ((parseInt(option.package_transfer.num_vehicles) || 1) *
                           (parseFloat(option.package_transfer.rate_per_vehicle) || 0) *
                           (1 + (parseFloat(option.package_transfer.gst_pct ?? 5)) / 100))
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Label className="text-xs text-cyan-600">Override:</Label>
                        <Input
                          type="number"
                          placeholder="Override total"
                          value={option.gross_override}
                          onChange={(e) => updateOption(option.id, 'gross_override', e.target.value)}
                          className="h-7 w-28 text-sm bg-cyan-50 border-cyan-200 text-right"
                          data-testid={`option-override-${optionIndex}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            )}

            {/* Collapsed Summary */}
            {option.collapsed && (
              <CardContent className="py-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    {option.accommodation?.filter(a => a.hotel_name).length || 0} hotels configured
                  </span>
                  <span className="font-semibold text-slate-800">
                    Gross: {formatCurrency(
                      option.gross_override ? parseFloat(option.gross_override) :
                      (option.accommodation?.reduce((sum, acc) => {
                        const cost = parseFloat(acc.room_rate_per_night) || 0;
                        const rooms = parseInt(acc.num_rooms) || 1;
                        const nights = parseInt(acc.nights) || 1;
                        const gstPct = parseFloat(acc.gst_pct ?? 12);
                        const base = cost * rooms * nights;
                        return sum + base + (base * gstPct / 100);
                      }, 0) || 0) + 
                      ((parseInt(option.package_transfer.num_vehicles) || 1) *
                       (parseFloat(option.package_transfer.rate_per_vehicle) || 0) *
                       (1 + (parseFloat(option.package_transfer.gst_pct ?? 5)) / 100))
                    )}
                  </span>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Comparison Table */}
      {manualData.options.length > 1 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
              <Calculator className="w-4 h-4 text-blue-600" />
              Quick Comparison
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-2 text-slate-600 font-medium"></th>
                    {manualData.options.map((opt, i) => (
                      <th key={opt.id} className="text-right p-2 text-slate-800 font-semibold">
                        {opt.option_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="p-2 text-slate-600">Accommodation (incl. GST)</td>
                    {manualData.options.map(opt => {
                      const accTotal = opt.accommodation?.reduce((sum, acc) => {
                        const cost = parseFloat(acc.room_rate_per_night) || 0;
                        const rooms = parseInt(acc.num_rooms) || 1;
                        const nights = parseInt(acc.nights) || 1;
                        const gstPct = parseFloat(acc.gst_pct ?? 12);
                        const base = cost * rooms * nights;
                        return sum + base + (base * gstPct / 100);
                      }, 0) || 0;
                      return (
                        <td key={opt.id} className="p-2 text-right text-slate-700">
                          {formatCurrency(accTotal)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2 text-slate-600">Package Transfer (incl. GST)</td>
                    {manualData.options.map(opt => {
                      const rate = parseFloat(opt.package_transfer.rate_per_vehicle) || 0;
                      const vehicles = parseInt(opt.package_transfer.num_vehicles) || 1;
                      const gstPct = parseFloat(opt.package_transfer.gst_pct ?? 5);
                      const base = rate * vehicles;
                      const total = base + (base * gstPct / 100);
                      return (
                        <td key={opt.id} className="p-2 text-right text-slate-700">
                          {formatCurrency(total)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="bg-slate-100">
                    <td className="p-2 font-semibold text-slate-800">GROSS TOTAL</td>
                    {manualData.options.map(opt => {
                      // Calculate accommodation with GST
                      const accTotal = opt.accommodation?.reduce((sum, acc) => {
                        const cost = parseFloat(acc.room_rate_per_night) || 0;
                        const rooms = parseInt(acc.num_rooms) || 1;
                        const nights = parseInt(acc.nights) || 1;
                        const gstPct = parseFloat(acc.gst_pct ?? 12);
                        const base = cost * rooms * nights;
                        return sum + base + (base * gstPct / 100);
                      }, 0) || 0;
                      
                      // Calculate transfer with GST
                      const rate = parseFloat(opt.package_transfer.rate_per_vehicle) || 0;
                      const vehicles = parseInt(opt.package_transfer.num_vehicles) || 1;
                      const gstPct = parseFloat(opt.package_transfer.gst_pct ?? 5);
                      const transferBase = rate * vehicles;
                      const transferTotal = transferBase + (transferBase * gstPct / 100);
                      
                      const gross = opt.gross_override ? parseFloat(opt.gross_override) : (accTotal + transferTotal);
                      return (
                        <td key={opt.id} className="p-2 text-right font-bold text-slate-800">
                          {formatCurrency(gross)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Miscellaneous Costs, Markup & GST will be configured in the next step
            </p>
          </CardContent>
        </Card>
      )}

      {/* Batch 1.D — Section B2: Miscellaneous Costs (Step 1)
          Mirrors the Step 3 preview editor so an agent can capture entry
          tickets, guide charges, etc. while building the package. Order
          matches the Step 3 preview (Misc → P2P) per UX-01. */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              Miscellaneous Costs
            </CardTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addMiscCost}
              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="manual-step1-add-misc-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Cost
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-3 pb-4">
          {miscCostsList.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-3">
              No miscellaneous costs added. Click "Add Cost" to include entry tickets, guide charges, etc.
            </p>
          ) : (
            <div className="space-y-2" data-testid="manual-step1-misc-list">
              {miscCostsList.map((cost, idx) => (
                <div
                  key={cost.id}
                  className={`p-3 rounded border ${cost.is_optional ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}
                  data-testid={`manual-step1-misc-row-${idx}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                    <div className="w-full sm:w-24 lg:w-24 flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!cost.is_optional}
                          onChange={(e) => updateMiscCost(cost.id, 'is_optional', !e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className={`text-xs font-medium ${!cost.is_optional ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {!cost.is_optional ? 'Included' : 'Optional'}
                        </span>
                      </label>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Description</Label>
                      <Input
                        placeholder="e.g., Entry tickets, Guide charges"
                        value={cost.remarks}
                        onChange={(e) => updateMiscCost(cost.id, 'remarks', e.target.value)}
                        className="h-9 text-sm bg-white"
                      />
                    </div>
                    <div className="w-full sm:w-28 lg:w-28">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Type</Label>
                      <Select value={cost.cost_type} onValueChange={(v) => updateMiscCost(cost.id, 'cost_type', v)}>
                        <SelectTrigger className="h-9 text-xs bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="total">Total</SelectItem>
                          <SelectItem value="per_person">Per Person</SelectItem>
                          <SelectItem value="per_day">Per Day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full sm:w-24 lg:w-24">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Amount</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={cost.amount}
                        onChange={(e) => updateMiscCost(cost.id, 'amount', parseFloat(e.target.value) || 0)}
                        className="h-9 text-sm bg-white"
                      />
                    </div>
                    <div className="w-full sm:w-20 lg:w-20">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">GST</Label>
                      <Select value={String(cost.gst_percentage || 0)} onValueChange={(v) => updateMiscCost(cost.id, 'gst_percentage', parseFloat(v))}>
                        <SelectTrigger className="h-9 text-xs bg-white"><SelectValue placeholder="GST" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">No GST</SelectItem>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeMiscCost(cost.id)}
                      className="h-9 w-9 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 border-t border-emerald-200">
                <span className="text-sm font-medium text-slate-700">Total Miscellaneous (Included)</span>
                <span className="text-sm font-bold text-slate-800" data-testid="manual-step1-misc-total">
                  {formatCurrency(miscIncludedTotal)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section C: P2P Transfers */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
              <ArrowRight className="w-4 h-4 text-orange-600" />
              P2P Transfers (Point to Point)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addP2PTransfer}
                className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="add-p2p-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add P2P
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setExpandedP2P(!expandedP2P)}
                className="h-7 w-7 p-0"
              >
                {expandedP2P ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        {expandedP2P && (
          <CardContent className="pt-4">
            {manualData.p2p_transfers.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">
                No P2P transfers added. Click "Add P2P" for airport/station pickups/drops.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b">From</th>
                      <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b">To</th>
                      <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b w-24">Mode</th>
                      <th className="text-left p-2 text-xs font-semibold text-slate-600 border-b w-24">Price Type</th>
                      <th className="text-center p-2 text-xs font-semibold text-slate-600 border-b w-14">Qty</th>
                      <th className="text-right p-2 text-xs font-semibold text-slate-600 border-b w-24">Cost/Unit</th>
                      <th className="text-center p-2 text-xs font-semibold text-slate-600 border-b w-16">GST%</th>
                      <th className="text-right p-2 text-xs font-semibold text-slate-600 border-b w-24">Total</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualData.p2p_transfers.map((p2p, idx) => {
                      const cost = parseFloat(p2p.cost) || 0;
                      const qty = parseInt(p2p.quantity) || 1;
                      const gstPct = parseFloat(p2p.gst_pct) || 0;
                      const baseTotal = cost * qty;
                      const gstAmount = baseTotal * (gstPct / 100);
                      const total = baseTotal + gstAmount;
                      
                      return (
                        <tr key={p2p.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-2">
                            <Input
                              type="text"
                              placeholder="e.g., Airport"
                              value={p2p.from_place}
                              onChange={(e) => updateP2PTransfer(p2p.id, 'from_place', e.target.value)}
                              className="h-8 text-sm bg-white"
                              data-testid={`p2p-from-${idx}`}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="text"
                              placeholder="e.g., Hotel"
                              value={p2p.to_place}
                              onChange={(e) => updateP2PTransfer(p2p.id, 'to_place', e.target.value)}
                              className="h-8 text-sm bg-white"
                              data-testid={`p2p-to-${idx}`}
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              value={p2p.transport_mode}
                              onValueChange={(v) => updateP2PTransfer(p2p.id, 'transport_mode', v)}
                            >
                              <SelectTrigger className="h-8 text-xs bg-white" data-testid={`p2p-mode-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Car">Car</SelectItem>
                                <SelectItem value="Train">Train</SelectItem>
                                <SelectItem value="Flight">Flight</SelectItem>
                                <SelectItem value="Bus">Bus</SelectItem>
                                <SelectItem value="Ferry">Ferry</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Select
                              value={p2p.price_type}
                              onValueChange={(v) => {
                                // Batch 1.E/F — when switching to Per Person,
                                // immediately set Qty = no_adults so the
                                // calculated total reflects "rate × adults".
                                // For FIXED, leave qty as-is (or seed 1).
                                updateP2PTransfer(p2p.id, 'price_type', v);
                                if (v === 'PER_PERSON') {
                                  const adults = parseInt(formData.no_adults) || 1;
                                  updateP2PTransfer(p2p.id, 'quantity', adults);
                                }
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs bg-white" data-testid={`p2p-pricetype-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="FIXED">Fixed</SelectItem>
                                <SelectItem value="PER_PERSON">Per Person</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            {/* Batch 1.E/F — Qty locks to `no_adults` when
                                price_type=PER_PERSON. Avoids the user having
                                to remember to type the pax count. Tooltip
                                explains the lock. */}
                            <Input
                              type="number"
                              min="1"
                              value={p2p.price_type === 'PER_PERSON' ? (parseInt(formData.no_adults) || 1) : p2p.quantity}
                              onChange={(e) => updateP2PTransfer(p2p.id, 'quantity', parseInt(e.target.value) || 1)}
                              disabled={p2p.price_type === 'PER_PERSON'}
                              title={p2p.price_type === 'PER_PERSON' ? 'Auto-set to number of adults for Per Person pricing' : 'Number of units'}
                              className={`h-8 text-sm text-center w-14 ${p2p.price_type === 'PER_PERSON' ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'}`}
                              data-testid={`p2p-qty-${idx}`}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              placeholder="0"
                              value={p2p.cost}
                              onChange={(e) => updateP2PTransfer(p2p.id, 'cost', e.target.value)}
                              className="h-8 text-sm text-right bg-cyan-50 border-cyan-200 w-24"
                              data-testid={`p2p-cost-${idx}`}
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              value={String(p2p.gst_pct ?? 5)}
                              onValueChange={(v) => updateP2PTransfer(p2p.id, 'gst_pct', parseFloat(v))}
                            >
                              <SelectTrigger className="h-8 text-xs bg-white w-16" data-testid={`p2p-gst-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="5">5%</SelectItem>
                                <SelectItem value="12">12%</SelectItem>
                                <SelectItem value="18">18%</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2 text-right text-slate-700">
                            {formatCurrency(total)}
                          </td>
                          <td className="p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeP2PTransfer(p2p.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`p2p-remove-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {manualData.p2p_transfers.length > 0 && (
                    <tfoot>
                      <tr className="bg-orange-50">
                        <td colSpan="7" className="p-2 text-right text-xs font-semibold text-slate-600">
                          P2P Total (incl. GST):
                        </td>
                        <td className="p-2 text-right font-semibold text-slate-800">
                          {formatCurrency(manualData.p2p_transfers.reduce((sum, p) => {
                            const cost = parseFloat(p.cost) || 0;
                            const qty = parseInt(p.quantity) || 1;
                            const gstPct = parseFloat(p.gst_pct) || 0;
                            const base = cost * qty;
                            return sum + base + (base * gstPct / 100);
                          }, 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Section D: Day-wise Itinerary */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="py-3 bg-gradient-to-r from-purple-50 to-violet-50 border-b border-purple-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
              <Calendar className="w-4 h-4 text-purple-600" />
              Day-wise Itinerary ({manualData.itinerary.length} days)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={autoGenerateItinerary}
                className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="auto-gen-itinerary-btn"
              >
                Auto-Generate
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addItineraryDay}
                className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="add-itinerary-day-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Day
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setExpandedItinerary(!expandedItinerary)}
                className="h-7 w-7 p-0"
              >
                {expandedItinerary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        {expandedItinerary && (
          <CardContent className="pt-4">
            {manualData.itinerary.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs text-slate-500 mb-3">
                  No itinerary configured. Click "Auto-Generate" to create from route or "Add Day" for manual entry.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {manualData.itinerary.map((day, idx) => {
                  const isLastDay = idx === manualData.itinerary.length - 1;
                  const isDepartureDay = day.title?.toLowerCase().includes('departure') || isLastDay;
                  
                  return (
                    <div key={idx} className="border border-slate-200 rounded-lg bg-white p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-600 text-white h-6 px-2">Day {day.day}</Badge>
                        <Input
                          value={day.title}
                          onChange={(e) => updateItinerary(idx, 'title', e.target.value)}
                          placeholder="Day title (e.g., Arrival Kochi - Munnar)"
                          className="flex-1 h-8 text-sm font-medium"
                          data-testid={`itinerary-title-${idx}`}
                        />
                        {/* Hide overnight dropdown for departure day */}
                        {!isDepartureDay && (
                          <Select
                            value={day.overnight_at || ''}
                            onValueChange={(v) => updateItinerary(idx, 'overnight_at', v)}
                          >
                            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`itinerary-overnight-${idx}`}>
                              <SelectValue placeholder="Overnight" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">No overnight</SelectItem>
                              {manualData.route.filter(s => s.place_name).map((stop, sIdx) => (
                                <SelectItem key={sIdx} value={stop.place_name}>{stop.place_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {isDepartureDay && (
                          <span className="text-xs text-slate-400 italic w-32 text-center">Departure</span>
                        )}
                        {manualData.itinerary.length > 1 && (
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteItineraryDay(idx)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`itinerary-remove-${idx}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={day.description}
                        onChange={(e) => updateItinerary(idx, 'description', e.target.value)}
                        placeholder="Day description and activities..."
                        className="min-h-[60px] text-xs"
                        data-testid={`itinerary-desc-${idx}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ManualPackageBuilder;
